import { MAINTENANCE_FETCH_TIMEOUT_MS, clampMessage } from './constants';
import { fetchRemoteFlag } from './remoteFlag';
import { getState, setState, type MaintenanceStatus } from './state';
import { resolveProbeUrl } from './urls';

const PROBE_DEBOUNCE_MS = 10_000;

/**
 * Active follow-up backoff schedule. When the damping counter increments
 * without reaching its threshold, the detector schedules a follow-up probe
 * at the next delay in this list. After 5 attempts the scheduler stops;
 * subsequent ambient triggers (visibility, online, retry storms) reset the
 * attempt counter on any state-resetting outcome.
 *
 * Why bounded: the recovery poller (poller.ts) takes over once entry to
 * 'maintenance' is achieved. The follow-up scheduler exists ONLY to close
 * the entry-detection gap (counter=1 → counter=2 transition without
 * relying on ambient triggers). Five attempts (~135s total) is enough to
 * confirm or rule out a real outage without burning indefinite cost.
 */
const FOLLOW_UP_DELAYS_MS = [5_000, 10_000, 20_000, 40_000, 60_000] as const;

let lastProbeAt = 0;
let inFlight: Promise<MaintenanceStatus> | null = null;
let consecutiveHealthFailures = 0;
let lastProbeReason: ProbeReason | null = null;
let followUpTimer: ReturnType<typeof setTimeout> | null = null;
let followUpAttempt = 0;

/**
 * Reason a probe was triggered. Travels into the Sentry breadcrumb data
 * for triage so operators can distinguish operator-click from poller-tick
 * from interceptor-driven retries.
 *
 * Debounce-bypass: `'manual'` (Try Again click) and `'poller'` (recovery
 * poller's 30s tick) intentionally bypass the 10-second debounce window.
 * The poller's interval dominates the debounce window, and we want each
 * tick to actually run a fresh probe rather than reuse a recent debounced
 * result. `maybeProbe`'s in-flight coalescing still protects against
 * poller-tick + detector-trigger races.
 *
 * Tier-1 (`'503'`) is split out from `'5xx'` because RFC 9110 defines 503
 * as "service unavailable due to a temporary overload or scheduled
 * maintenance" - much stronger evidence than a generic 5xx (500/502/504),
 * which could be an endpoint-specific bug. `THRESHOLD_BY_REASON` below
 * uses this distinction: a `'503'` trigger needs only one corroborating
 * /health failure to flip to maintenance, while other reasons need two.
 */
export type ProbeReason = '503' | '5xx' | 'network-error' | 'manual' | 'visibility' | 'poller';

/**
 * Number of consecutive /health failures required before flipping to
 * 'maintenance' via the damping path. Indexed by the reason that triggered
 * the probe.
 *
 * Tier-1 ('503'): single corroboration suffices because the trigger
 * itself is RFC 9110's explicit "service unavailable" signal.
 * Tier-2 (everything else): two corroborations to filter false positives
 * from flaky networks, endpoint-specific 5xx bugs, and user-side glitches.
 *
 * Authoritative entry signals (/health 503 status, /health body
 * maintenance:true, /api/maintenance returning maintenance:true) bypass
 * this damping entirely and flip immediately on the first probe.
 */
const THRESHOLD_BY_REASON: Readonly<Record<ProbeReason, number>> = {
  '503': 1,
  '5xx': 2,
  'network-error': 2,
  manual: 2,
  visibility: 2,
  poller: 2,
} as const;

/**
 * Discriminated union for probe-fetch outcomes. The two cases are:
 *
 * - `{ kind: 'ok', status, body }`: the request resolved with an HTTP
 *   response. `status` may be any code (including 5xx); the caller decides
 *   whether to treat it as a failure signal. `body` is parsed JSON or `null`
 *   if the response was not JSON-parseable.
 * - `{ kind: 'failed', reason }`: the request never produced a response.
 *   `'aborted'` covers our own timeout firing the AbortController.
 *   `'network'` covers fetch transport failures (DNS, CORS, CSP) AND any
 *   other unexpected throwable, since in practice everything fetch can
 *   throw that isn't AbortError is a transport-layer wrapper. The reason
 *   discriminator feeds the Sentry breadcrumb data for triage without
 *   leaking the raw error object across module boundaries.
 *
 * Using a discriminated union eliminates the previously-possible impossible
 * states (e.g., `{ ok: true, failed: true, status: 0 }`) and forces every
 * caller to narrow before reading status/body, which the compiler now
 * enforces at every consumer.
 */
type FetchProbeResult =
  { kind: 'ok'; status: number; body: unknown } | { kind: 'failed'; reason: 'aborted' | 'network' };

async function fetchWithTimeout(
  input: string,
  timeoutMs: number,
  mode: RequestMode = 'cors',
): Promise<FetchProbeResult> {
  const controller = new AbortController();
  const id = setTimeout(() => {
    controller.abort();
  }, timeoutMs);
  try {
    const res = await fetch(input, {
      method: 'GET',
      // Favicon and /health both want fresh signals; /api/maintenance
      // has its own fetch in remoteFlag.ts that uses cache: 'default'
      // to honor the Pages Function's max-age=30 swr=60 contract.
      cache: 'no-store',
      mode,
      signal: controller.signal,
    });
    let body: unknown = null;
    try {
      body = await res.clone().json();
    } catch {
      body = null;
    }
    return { kind: 'ok', status: res.status, body };
  } catch (e) {
    // Discriminate aborted (our own timeout) from any other transport
    // failure. Everything else lands in 'network' as a defensive default
    // because every non-Abort throwable fetch produces is, in practice,
    // a transport-layer wrapper (DNS, CORS, CSP, mixed-content).
    if (e instanceof DOMException && e.name === 'AbortError') {
      return { kind: 'failed', reason: 'aborted' };
    }
    return { kind: 'failed', reason: 'network' };
  } finally {
    clearTimeout(id);
  }
}

/**
 * Fire a Sentry breadcrumb describing a probe outcome. Fire-and-forget;
 * Sentry SDK throws are swallowed so observability never blocks state
 * propagation. Mirrors the pattern in state.ts around setState's breadcrumb
 * call (search for `addBreadcrumb` in state.ts).
 */
function probeBreadcrumb(message: string, data: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  try {
    window.Sentry?.addBreadcrumb?.({
      category: 'maintenance',
      message,
      level: 'info',
      data,
    });
  } catch {
    // Sentry SDK throwing must never break the probe path.
  }
}

/**
 * Schedule a follow-up probe to confirm or rule out a damping-incremented
 * counter. Cancels any previously-scheduled follow-up first (so the latest
 * trigger wins). Stops after FOLLOW_UP_DELAYS_MS.length attempts; subsequent
 * outcomes that reset the counter (success, state-change) clear the attempt
 * counter so a fresh trigger can re-arm.
 *
 * The follow-up calls `maybeProbe` with `bypassDebounce: true` because the
 * follow-up scheduler IS the throttling mechanism (its backoff is the
 * cadence). The 10s `PROBE_DEBOUNCE_MS` would otherwise suppress
 * intentional re-probes within 10s of a recent probe.
 *
 * In-flight coalescing in `maybeProbe` still folds simultaneous triggers
 * (e.g., follow-up firing while a manual click runs).
 */
function scheduleFollowUp(reason: ProbeReason): void {
  if (followUpTimer !== null) {
    clearTimeout(followUpTimer);
    followUpTimer = null;
  }
  if (followUpAttempt >= FOLLOW_UP_DELAYS_MS.length) {
    probeBreadcrumb('probe follow-up canceled', { cause: 'attempts-exhausted', reason });
    return;
  }
  const delayMs = FOLLOW_UP_DELAYS_MS[followUpAttempt];
  const attemptNumber = followUpAttempt + 1;
  followUpAttempt++;
  probeBreadcrumb('probe follow-up scheduled', {
    reason,
    attempt: attemptNumber,
    delayMs,
  });
  followUpTimer = setTimeout(() => {
    followUpTimer = null;
    probeBreadcrumb('probe follow-up fired', { reason, attempt: attemptNumber });
    void maybeProbe(reason, { bypassDebounce: true });
  }, delayMs);
}

/**
 * Cancel any pending follow-up and reset the attempt counter. Called from
 * every state-resetting code path so the scheduler never leaks across
 * state transitions or HMR re-evaluations.
 */
export function clearFollowUp(cause: 'state-change' | 'success' | 'hmr'): void {
  if (followUpTimer !== null) {
    clearTimeout(followUpTimer);
    followUpTimer = null;
    probeBreadcrumb('probe follow-up canceled', { cause });
  }
  followUpAttempt = 0;
}

/**
 * Run the discriminator probe and write the result to state.ts.
 * Returns the new status. Does not respect debounce; callers that want
 * debouncing should use `maybeProbe`.
 */
export async function runProbe(): Promise<MaintenanceStatus> {
  // 1. Fast-path: navigator.onLine
  if (!navigator.onLine) {
    // consecutiveHealthFailures intentionally NOT reset here. Offline is a
    // transient user-side condition orthogonal to backend health damping.
    // If the backend is actually failing AND the user briefly drops offline,
    // the failure count must survive so the next probe can correctly trip
    // the threshold. Counter only resets on a confirmed successful /health
    // response.
    clearFollowUp('state-change');
    setState({ status: 'offline', customMessage: '' });
    return 'offline';
  }

  // 2. Discriminator: can the user reach our static origin?
  //
  // Use 'no-cors' for the favicon: on Capacitor native the request is
  // cross-origin to the web domain (e.g., dev-app.meetball.fun) and
  // Cloudflare static assets do not send Access-Control-Allow-Origin, so a
  // default 'cors' fetch would reject with TypeError and incorrectly flip
  // every native user to 'connection'. With 'no-cors' the response is
  // opaque (status 0, body unreadable), but we only care that the request
  // resolved (network reachable) vs threw (network broken). Status 404 on
  // the wire (no /favicon.ico file ships in this build - only
  // favicon-32x32.png) is irrelevant: an opaque 404 still resolves rather
  // than throwing, which is the only signal the discriminator inspects.
  //
  // Cache-buster ?probe=${Date.now()} guards against the browser's HTTP
  // cache: without it, a fresh-enough cached response (e.g., 404 with no
  // explicit no-cache headers) could resolve from cache even when the
  // network is unreachable, defeating the reachability test. The query
  // parameter forces a unique cache key per probe. cache: 'no-store' (set
  // in fetchWithTimeout) reinforces by bypassing the cache entirely. The
  // service worker (vite.config.ts globPatterns) has no /favicon.ico in
  // the precache manifest because no .ico file exists in the build, so
  // requests pass straight through to the network.
  const origin = await fetchWithTimeout(
    `${resolveProbeUrl('/favicon.ico')}?probe=${Date.now()}`,
    MAINTENANCE_FETCH_TIMEOUT_MS,
    'no-cors',
  );
  if (origin.kind === 'failed') {
    // Counter NOT reset: a transient origin failure (e.g., user steps onto
    // a train, favicon momentarily fails) is orthogonal to backend health
    // damping. Without this guard, an outage interleaved with brief
    // connection flaps would require extra failures to surface.
    probeBreadcrumb('probe failed at origin', {
      step: 'origin',
      reason: origin.reason,
    });
    clearFollowUp('state-change');
    setState({ status: 'connection', customMessage: '' });
    return 'connection';
  }

  // 3. Fetch BOTH operator flag and backend health before deciding. Each
  // signal is independent; we apply a strict precedence ordering below to
  // resolve cases where they disagree (e.g., operator says clear but
  // backend is still 503). Sequential fetches keep the code simple and the
  // small added latency vs Promise.all is acceptable - both endpoints are
  // edge-cached or backend-cheap.
  const remote = await fetchRemoteFlag();
  const health = await fetchWithTimeout(resolveProbeUrl('/health'), MAINTENANCE_FETCH_TIMEOUT_MS);

  const currentStatus = getState().status;

  // Helper: does /health actively contradict an operator-flag-clear exit?
  // Transport failure ('failed') and 5xx-non-503 are real "backend looks
  // broken" signals - we should NOT exit on operator-clear when these
  // fire (the backend is in trouble even if the operator hasn't
  // acknowledged it). 4xx (auth/CORS/route-missing) and 1xx/3xx do NOT
  // contradict - those are "no positive backend signal" states where the
  // operator's authoritative clear should win.
  const healthContradictsExit =
    health.kind === 'failed' ||
    (health.kind === 'ok' && health.status >= 500 && health.status !== 503);

  // Precedence ordering. Authoritative entry signals (5a/5b/5c) preempt
  // all exits. Backend-authoritative exit (5d) preempts operator-conditional
  // exit (5e). Damping (5f) only runs when no authoritative signal fired.
  // Default (5g) preserves state when no signal applies.

  // 5a. AUTHORITATIVE ENTRY: /health 503.
  // The backend is up enough to respond AND is telling us it's in
  // maintenance. Flip immediately, no threshold needed; honor the body's
  // message. Beats operator-clear because backend authority wins.
  if (health.kind === 'ok' && health.status === 503) {
    probeBreadcrumb('probe flagged via health 503', {
      step: 'health',
      status: 503,
    });
    clearFollowUp('state-change');
    setState({
      status: 'maintenance',
      customMessage: clampMessage(
        health.body && typeof health.body === 'object'
          ? (health.body as { message?: unknown }).message
          : '',
      ),
    });
    return 'maintenance';
  }

  // 5b. AUTHORITATIVE ENTRY: /health 2xx with body.maintenance:true.
  // Backend body-channel maintenance signal. Beats operator-clear for
  // the same reason as 5a.
  if (
    health.kind === 'ok' &&
    health.status >= 200 &&
    health.status < 300 &&
    health.body &&
    typeof health.body === 'object' &&
    (health.body as { maintenance?: unknown }).maintenance === true
  ) {
    probeBreadcrumb('probe flagged via health body', {
      step: 'health',
      status: health.status,
    });
    consecutiveHealthFailures = 0; // 2xx confirms reachability
    clearFollowUp('state-change');
    setState({
      status: 'maintenance',
      customMessage: clampMessage((health.body as { message?: unknown }).message),
    });
    return 'maintenance';
  }

  // 5c. OPERATOR ENTRY: /api/maintenance:true.
  // Edge cache may lag a flip-on by up to 30s, but once the cache catches
  // up this fires. If the backend is also in trouble (5xx) we'd have hit
  // 5a/5b first.
  if (remote && remote.maintenance) {
    probeBreadcrumb('probe flagged via remote', {
      step: 'remote-flag',
      hasMessage: remote.message.length > 0,
    });
    clearFollowUp('state-change');
    setState({ status: 'maintenance', customMessage: remote.message });
    return 'maintenance';
  }

  // 5d. AUTHORITATIVE EXIT: /health 2xx with body.maintenance:false.
  // Backend explicitly confirms it's healthy. Reset counter, exit.
  if (
    health.kind === 'ok' &&
    health.status >= 200 &&
    health.status < 300
    // Implicit: body.maintenance !== true (5b would have caught it).
  ) {
    consecutiveHealthFailures = 0;
    clearFollowUp('success');
    if (currentStatus === 'maintenance') {
      probeBreadcrumb('probe exit via health body', {
        step: 'health',
        status: health.status,
      });
    }
    setState({ status: 'ready', customMessage: '' });
    return 'ready';
  }

  // 5e. OPERATOR CONDITIONAL EXIT (Bug 2 fix).
  // Fires when: operator explicitly says false AND we're currently showing
  // a takeover AND /health is NOT actively contradicting. The conditional
  // is what makes this safe: a transport-failed or 5xx-non-503 /health is
  // real "backend looks broken" evidence and blocks the exit (5f handles
  // it as damping). 4xx (auth/CORS misconfig, route missing - the original
  // Bug 2 reproducer) is "no positive evidence" and trusts the
  // operator's authoritative clear.
  if (remote && !remote.maintenance && currentStatus === 'maintenance' && !healthContradictsExit) {
    consecutiveHealthFailures = 0;
    clearFollowUp('success');
    probeBreadcrumb('probe exit via operator flag', {
      step: 'remote-flag',
      healthKind: health.kind,
      healthStatus: health.kind === 'ok' ? health.status : null,
    });
    setState({ status: 'ready', customMessage: '' });
    return 'ready';
  }

  // 5f. DAMPING: /health failed OR 5xx-non-503.
  // Single transient failures shouldn't flip a healthy user into the
  // takeover, but two consecutive failures (or one for tier-1 '503'
  // triggers) signal a real outage. Below threshold, schedule an active
  // follow-up if the trigger reason carries evidence.
  if (
    health.kind === 'failed' ||
    (health.kind === 'ok' && health.status >= 500 && health.status !== 503)
  ) {
    consecutiveHealthFailures++;
    const threshold = THRESHOLD_BY_REASON[lastProbeReason ?? '5xx'];
    probeBreadcrumb('probe health failure (damping)', {
      step: 'health',
      ...(health.kind === 'failed' ? { reason: health.reason } : { status: health.status }),
      consecutiveFailures: consecutiveHealthFailures,
      tier: threshold,
      triggerReason: lastProbeReason ?? null,
    });
    if (consecutiveHealthFailures >= threshold) {
      if (lastProbeReason === '503' && threshold === 1 && consecutiveHealthFailures === 1) {
        probeBreadcrumb('probe tier-1 fast entry', {
          step: 'health',
          reason: '503',
          threshold: 1,
        });
      }
      clearFollowUp('state-change');
      // Do NOT pass `customMessage: ''` here. If we're already in
      // 'maintenance' (e.g., the recovery poller ticked twice with /health
      // 500-not-503 while the user is still seeing a custom message from the
      // original 5a/5b/5c entry), an explicit '' would wipe the visible
      // operator message on the second damping tick. Letting state.ts's
      // undefined-filter preserve the prior `customMessage` is correct here:
      // - First entry from 'ready': prevMessage is '' → stays ''.
      // - Re-entry while already in 'maintenance': prevMessage is preserved.
      // 5a/5b/5c remain the authoritative source for fresh messages.
      setState({ status: 'maintenance' });
      return 'maintenance';
    }
    if (
      lastProbeReason === '503' ||
      lastProbeReason === '5xx' ||
      lastProbeReason === 'network-error'
    ) {
      scheduleFollowUp(lastProbeReason);
    }
    return currentStatus;
  }

  // 5g. DEFAULT: preserve current state.
  // Reached when /health is 1xx/3xx/4xx AND the operator-conditional-exit
  // (5e) didn't fire (e.g., currently in 'ready' or remote was true/null).
  // Conservative non-2xx response handling: don't flip to 'ready' just
  // because the server responded - we don't have a positive maintenance:false
  // signal. Don't increment damping counter either - the server actually
  // sent a response, just an ambiguous one.
  if (health.kind === 'ok' && (health.status < 200 || health.status >= 300)) {
    probeBreadcrumb('probe health ambiguous (non-2xx, non-503)', {
      step: 'health',
      status: health.status,
    });
  }
  return currentStatus;
}

/**
 * Debounced wrapper around runProbe. Multiple triggers within
 * PROBE_DEBOUNCE_MS resolve to the existing in-flight result (or skip
 * entirely if a probe ran recently).
 *
 * The optional `bypassDebounce` opt is for the active follow-up scheduler
 * (`scheduleFollowUp`) which has its own backoff cadence and should not be
 * suppressed by the standard debounce. In-flight coalescing still applies
 * regardless - a follow-up that fires while a probe is already running
 * folds into the existing result rather than starting a fresh chain.
 */
export async function maybeProbe(
  reason: ProbeReason,
  opts?: { bypassDebounce?: boolean },
): Promise<MaintenanceStatus> {
  const now = Date.now();

  // Coalesce concurrent triggers
  if (inFlight) return inFlight;

  // Skip if we just probed. `'manual'` (operator Try Again click) and
  // `'poller'` (recovery poller tick) bypass the debounce window: an
  // impatient user clicking Try Again should not be silently throttled,
  // and the 30s poller interval dominates the 10s debounce window so each
  // tick must run a fresh probe. The follow-up scheduler also bypasses
  // (via `opts.bypassDebounce`) for the same reason - its backoff IS the
  // throttling mechanism.
  const reasonBypassesDebounce = reason === 'manual' || reason === 'poller';
  const explicitBypass = opts?.bypassDebounce === true;
  const shouldBypass = reasonBypassesDebounce || explicitBypass;
  if (now - lastProbeAt < PROBE_DEBOUNCE_MS && !shouldBypass) {
    // Last probe is authoritative within the debounce window; return current state
    // honestly so any awaiting caller sees the real status, not a stub 'ready'.
    return getState().status;
  }

  lastProbeAt = now;
  lastProbeReason = reason;
  inFlight = runProbe();
  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}

/** Test-only: reset module state. */
export function _resetForTests(): void {
  lastProbeAt = 0;
  inFlight = null;
  consecutiveHealthFailures = 0;
  lastProbeReason = null;
  if (followUpTimer !== null) {
    clearTimeout(followUpTimer);
    followUpTimer = null;
  }
  followUpAttempt = 0;
}
